import { DATE_MONTH_YEAR } from "PFCore/helpers/date";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { useMemo } from "react";

import { useCalendarGridScope } from "../../hooks";
import { addDays, setTimeToMidnight } from "../calendar/calendar.utils";

export type Month = { label: string; date: Date; left: number; width: number };
export type Week = { isoWeek: number; startDate: Date; endDate: Date; left: number; width: number };
export type Day = { date: Date; left: number; width: number };

const getStartEndOfWeek = (dayDate: Date): { startDate: Date; endDate: Date } => {
  const currentDayDate = setTimeToMidnight(new Date(dayDate));
  const daysAfterMonday = (currentDayDate.getUTCDay() + 6) % 7; // Adding 6 before modulo to handle Sunday as the last day
  const startDate = addDays(currentDayDate, -1 * daysAfterMonday);
  const endDate = addDays(new Date(startDate), 6);
  return { startDate, endDate };
};

interface UseCalendarDays {
  dayWidth: number;
}

interface UseCalendarDaysReturn {
  days: Day[];
  months: Month[];
  weeks: Week[];
}

export const useCalendarDays = ({ dayWidth }: UseCalendarDays): UseCalendarDaysReturn => {
  const { min: startDate, max: endDate } = useCalendarGridScope();
  const { utc } = useDateFormatter();

  const days = useMemo<Day[]>(() => {
    if (!dayWidth) {
      return [];
    }
    const result: Day[] = [];

    const start = new Date(startDate);
    const end = new Date(endDate);
    let counter = 0;
    for (let currentDate = start; currentDate <= end; addDays(currentDate, 1)) {
      const date = new Date(currentDate.getTime());
      result.push({
        date,
        left: counter * dayWidth,
        width: dayWidth
      });
      counter++;
    }

    return result;
  }, [startDate, endDate, dayWidth]);

  const weeks = useMemo<Week[]>(() => {
    const weeks: Omit<Week, "width">[] = [];
    days.forEach((_, dayIndex) => {
      const { date: dayDate, left } = days[dayIndex];
      const isoWeek = utc(dayDate).isoWeek();
      const prevWeek = weeks[weeks.length - 1];
      if (!prevWeek || isoWeek !== prevWeek.isoWeek) {
        const { startDate, endDate } = getStartEndOfWeek(dayDate);
        weeks.push({ isoWeek, startDate, endDate, left });
      }
    });
    return weeks.map((week, index) => {
      const { left } = week;
      const nextWeekLeft = weeks[index + 1]?.left ?? days[days.length - 1].left + dayWidth;
      return {
        ...week,
        width: nextWeekLeft - left
      };
    });
  }, [days, dayWidth, utc]);

  const months = useMemo<Month[]>(() => {
    const months: { label: string; date: Date; left: number }[] = [];

    for (const { date: dayDate, left } of days) {
      const label = utc(dayDate).format(DATE_MONTH_YEAR);
      const prevMonth = months[months.length - 1];
      if (months.length === 0 || !prevMonth || label !== prevMonth.label) {
        const date = setTimeToMidnight(new Date(dayDate));
        date.setDate(1);
        months.push({ label, date, left });
      }
    }
    return months.map((month, index) => {
      const { left } = month;
      const nextMonthLeft = months[index + 1]?.left ?? days[days.length - 1].left + dayWidth;
      return {
        ...month,
        width: nextMonthLeft - left
      };
    });
  }, [days, dayWidth, utc]);

  return {
    days,
    months,
    weeks
  };
};
